import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const EmailTemplatesPage = () => {
  const { getToken } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [copiedId, setCopiedId] = useState(null);
    const [expandedIds, setExpandedIds] = useState(new Set());
  const [previewData, setPreviewData] = useState({
    name: 'John Smith',
    company: 'Acme Corp',
    service: 'Product Engineering'
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://website-476522.uc.r.appspot.com';
      const response = await fetch(`${apiUrl}/api/admin/email-templates`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const replaceVariables = (text) => {
    return text
      .replace(/\{\{name\}\}/g, previewData.name)
      .replace(/\{\{company\}\}/g, previewData.company)
      .replace(/\{\{service\}\}/g, previewData.service);
  };

  const handleCopy = (templateId, text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(templateId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleExpanded = (templateId) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  const categories = [
    { value: 'all', label: 'All Templates', icon: '✉️' },
    { value: 'initial-outreach', label: 'Initial Outreach', icon: '👋' },
    { value: 'follow-up', label: 'Follow-up', icon: '🔄' },
    { value: 'proposal', label: 'Proposal', icon: '📄' },
    { value: 're-engagement', label: 'Re-engagement', icon: '🔥' },
    { value: 'thank-you', label: 'Thank You', icon: '🙏' }
  ];

  const filteredTemplates = selectedCategory === 'all'
    ? templates
    : templates.filter(t => t.category === selectedCategory);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-nunito text-white">Email Templates</h1>
        <p className="mt-1 text-orange-300 font-nunito">
          Cold email templates for different scenarios
        </p>
      </div>

      {/* Preview Data Controls */}
      <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-6">
        <h2 className="text-lg font-bold font-nunito text-white mb-4">Preview Variables</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
              Name
            </label>
            <input
              type="text"
              value={previewData.name}
              onChange={(e) => setPreviewData({ ...previewData, name: e.target.value })}
              className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white font-nunito focus:outline-none focus:border-amber-500/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
              Company
            </label>
            <input
              type="text"
              value={previewData.company}
              onChange={(e) => setPreviewData({ ...previewData, company: e.target.value })}
              className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white font-nunito focus:outline-none focus:border-amber-500/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
              Service
            </label>
            <input
              type="text"
              value={previewData.service}
              onChange={(e) => setPreviewData({ ...previewData, service: e.target.value })}
              className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white font-nunito focus:outline-none focus:border-amber-500/50"
            />
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-3">
        {categories.map((cat) => (
          <button
            key={cat.value}
            onClick={() => setSelectedCategory(cat.value)}
            className={`px-4 py-2 rounded-lg font-nunito font-medium transition flex items-center gap-2 ${
              selectedCategory === cat.value
                ? 'bg-amber-500 text-black'
                : 'bg-orange-900/40 border border-white/10 text-orange-100 hover:bg-white/10'
            }`}
          >
            <span>{cat.icon}</span>
            {cat.label}
          </button>
        ))}
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 gap-6">
        {filteredTemplates.map((template) => {
          const isExpanded = expandedIds.has(template.id);
          return (
            <div key={template.id} className="bg-orange-900/40 border border-white/10 rounded-2xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleExpanded(template.id)}
                    className="text-amber-500 hover:text-amber-400 transition"
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    <span className="text-2xl">{isExpanded ? '▼' : '▶'}</span>
                  </button>
                  <div>
                    <h3 className="text-xl font-bold text-white font-nunito">{template.name}</h3>
                    <p className="text-sm text-orange-400 font-nunito mt-1">
                      {categories.find(c => c.value === template.category)?.label}
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleCopy(template.id, template.body)}
                className="px-4 py-2 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-600 transition font-nunito flex items-center gap-2"
              >
                <span>{copiedId === template.id ? '✓' : '📋'}</span>
                {copiedId === template.id ? 'Copied!' : 'Copy'}
              </button>
            </div>

            {isExpanded && (
              <div className="space-y-3 mt-4">
              <div className="bg-black/30 border border-white/10 rounded-lg p-4">
                <p className="text-xs text-orange-400 font-nunito mb-2">SUBJECT:</p>
                <p className="text-white font-nunito font-medium">
                  {replaceVariables(template.subject)}
                </p>
              </div>

              <div className="bg-black/30 border border-white/10 rounded-lg p-4">
                <p className="text-xs text-orange-400 font-nunito mb-2">BODY:</p>
                <pre className="text-orange-100 font-nunito whitespace-pre-wrap text-sm leading-relaxed">
                  {replaceVariables(template.body)}
                </pre>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-xs text-amber-300 font-nunito">
                  <strong>Variables:</strong> {template.variables || '{{name}}, {{company}}, {{service}}'}
                </p>
              </div>
            </div>
          )}
          </div>
          );
        })}

        {filteredTemplates.length === 0 && (
          <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-12 text-center">
            <p className="text-orange-400 font-nunito">No templates found in this category.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailTemplatesPage;
